import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { VisibilityBadge } from 'components/blocks/visibility-field';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import {
  useDisconnectComposioConnection,
  useGetComposioConnections,
  useGetComposioToolkitCatalog,
  useGetComposioToolkits,
  useGetToolById,
  useInitiateComposioConnection,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { cn } from 'lib/utils';
import { resolveComposioToolLogo } from './composio-logos';
import {
  getComposioConfig,
  getToolEditSubject,
  getToolVisibility,
  normalizeToolDetails,
} from './tool-detail-utils';

const PENDING_TIMEOUT_MS = 2 * 60 * 1000;
const VISIBLE_ACTIONS_COUNT = 7;
const MIN_DESCRIPTION_LENGTH = 24;

type ComposioOauthMessage = {
  type?: string;
  ok?: boolean;
  toolId?: string;
  toolkit?: string;
};

type ConnectorDetailsModalProps = {
  tool: ITool | null;
  projectKey: string;
  onClose: () => void;
};

function formatActionLabel(slug: string, toolkit: string): string {
  const normalizedToolkit = toolkit.trim().toUpperCase();
  const withoutPrefix = slug
    .replace(new RegExp(`^${normalizedToolkit}_`, 'i'), '')
    .replace(/^[A-Z0-9]+_/i, '');
  return withoutPrefix.toLowerCase();
}

function formatLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function resolveDescription(toolDescription?: string, catalogDescription?: string | null): string | null {
  const tool = toolDescription?.trim() ?? '';
  const catalog = catalogDescription?.trim() ?? '';

  if (catalog.length >= MIN_DESCRIPTION_LENGTH) return catalog;
  if (tool.length >= MIN_DESCRIPTION_LENGTH) return tool;
  if (catalog.length > 0) return catalog;
  if (tool.length >= 10) return tool;

  return null;
}

function ActionChip({ label }: Readonly<{ label: string }>) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted/70 px-2.5 py-1 text-[11px] text-foreground/85">
      <Check className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
      <span className="truncate font-mono lowercase">{label}</span>
    </span>
  );
}

function ConnectorDetailsSkeleton() {
  return (
    <div className="space-y-6 p-7" aria-busy="true" aria-live="polite">
      <div className="flex items-start gap-4 pr-8">
        <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-7 w-20 shrink-0 rounded-full" />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-[92%]" />
        <Skeleton className="h-4 w-4/5" />
      </div>

      <div className="space-y-3">
        <Skeleton className="h-4 w-36" />
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-7 w-28 rounded-full" />
          <Skeleton className="h-7 w-36 rounded-full" />
          <Skeleton className="h-7 w-24 rounded-full" />
          <Skeleton className="h-7 w-32 rounded-full" />
          <Skeleton className="h-7 w-40 rounded-full" />
          <Skeleton className="h-7 w-28 rounded-full" />
        </div>
      </div>

      <div className="space-y-4 border-t border-border/70 pt-5">
        <Skeleton className="h-4 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-4 w-28" />
        </div>
      </div>

      <div className="space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
      </div>
    </div>
  );
}

function ConnectorLogo({
  logo,
  name,
  toolkit,
}: Readonly<{
  logo?: string | null;
  name: string;
  toolkit?: string | null;
}>) {
  const resolvedLogo = resolveComposioToolLogo(toolkit) || logo;

  if (resolvedLogo) {
    return (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#E7E7E7] bg-white p-0.5 shadow-sm dark:border-border dark:bg-card">
        <img src={resolvedLogo} alt="" className="h-full w-full rounded-full object-cover" />
      </span>
    );
  }

  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#E7E7E7] bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground dark:border-border">
      {name.slice(0, 2)}
    </span>
  );
}

export function ConnectorDetailsModal({ tool, projectKey, onClose }: Readonly<ConnectorDetailsModalProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showAllActions, setShowAllActions] = useState(false);
  const [pendingToolkit, setPendingToolkit] = useState<string | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toolId = tool?.id ?? '';
  const isOpen = Boolean(tool);

  const toolQuery = useGetToolById({ toolId, projectKey }, { enabled: isOpen && !!projectKey });
  const catalogQuery = useGetComposioToolkitCatalog({ enabled: isOpen });
  const connectionsQuery = useGetComposioConnections(toolId, {
    enabled: isOpen && !!toolId,
    refetchOnWindowFocus: true,
    refetchInterval: pendingToolkit ? 4000 : false,
  });
  const toolkitsQuery = useGetComposioToolkits(toolId, { enabled: isOpen && !!toolId });

  const initiateMutation = useInitiateComposioConnection();
  const disconnectMutation = useDisconnectComposioConnection();

  const toolDetails = useMemo(
    () => normalizeToolDetails(toolQuery.data, tool),
    [tool, toolQuery.data]
  );
  const isOwner = useIsOwner(getToolEditSubject(toolQuery.data, toolDetails ?? tool));
  const isPublicTool = getToolVisibility(toolDetails ?? tool) === 'public';
  const canManageConnection = isPublicTool || isOwner;
  const { toolkits, tools: allowlistedTools } = getComposioConfig(toolDetails);
  const primaryToolkit = toolkits[0] ?? '';

  const catalogEntry = useMemo(() => {
    if (!primaryToolkit) return null;
    const normalized = primaryToolkit.trim().toUpperCase();
    return (
      catalogQuery.data?.toolkits.find(
        (entry) => entry.slug.trim().toUpperCase() === normalized
      ) ?? null
    );
  }, [catalogQuery.data, primaryToolkit]);

  const connections = connectionsQuery.data?.connections ?? {};
  const connectionEntry = primaryToolkit ? connections[primaryToolkit] : undefined;
  const isConnected = connectionEntry?.connected === true;

  const availability = useMemo(() => {
    const map = new Map<
      string,
      { authConfigAvailable: boolean; reason?: string; authConfigId?: string }
    >();
    for (const entry of toolkitsQuery.data?.toolkits ?? []) {
      map.set(entry.toolkit, {
        authConfigAvailable: entry.authConfigAvailable,
        reason: entry.reason,
        authConfigId: entry.authConfigId,
      });
    }
    return map;
  }, [toolkitsQuery.data]);

  const toolkitAvailability = primaryToolkit ? availability.get(primaryToolkit) : undefined;
  const authConfigMissing = toolkitAvailability?.authConfigAvailable === false;
  const isPendingThis = pendingToolkit === primaryToolkit;
  const busy =
    (initiateMutation.isPending || disconnectMutation.isPending) &&
    (initiateMutation.variables?.toolkit === primaryToolkit ||
      disconnectMutation.variables?.toolkit === primaryToolkit);

  const actionLabels = useMemo(() => {
    if (!allowlistedTools?.length) return [];
    return allowlistedTools.map((slug) => formatActionLabel(slug, primaryToolkit));
  }, [allowlistedTools, primaryToolkit]);

  const visibleActions = showAllActions
    ? actionLabels
    : actionLabels.slice(0, VISIBLE_ACTIONS_COUNT);
  const hasMoreActions = actionLabels.length > VISIBLE_ACTIONS_COUNT;

  const displayName = catalogEntry?.name || formatLabel(primaryToolkit) || toolDetails?.name || tool?.name || '';
  const displayDescription = resolveDescription(toolDetails?.description, catalogEntry?.description);
  const categoryLabel = catalogEntry?.categories?.[0]
    ? formatLabel(catalogEntry.categories[0])
    : t('CONNECTOR', { defaultValue: 'Connector' });

  const actionsHeading = allowlistedTools?.length
    ? t('ACTIONS_ENABLED', {
      defaultValue: '{{count}} actions enabled',
      count: allowlistedTools.length,
    })
    : null;

  const clearPending = () => {
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    setPendingToolkit(null);
  };

  useEffect(() => {
    if (!isOpen) {
      setShowAllActions(false);
      clearPending();
    }
  }, [isOpen]);

  useEffect(() => () => clearPending(), []);

  useEffect(() => {
    if (!toolId) return;

    const handleMessage = (event: MessageEvent<ComposioOauthMessage>) => {
      const data = event.data;
      if (data?.type !== 'composio-oauth') {
        return;
      }
      if (data.toolId && data.toolId !== toolId) {
        return;
      }
      clearPending();
      queryClient.invalidateQueries({ queryKey: ['tools', 'composio-connections', toolId] });
      if (data.ok) {
        toast({
          variant: 'success',
          title: t('CONNECTED', { defaultValue: 'Connected' }),
          description: `${data.toolkit ?? ''} ${t('ACCOUNT_CONNECTED_SUCCESSFULLY', { defaultValue: 'account connected successfully.' })}`.trim(),
        });
      } else {
        toast({
          variant: 'destructive',
          title: t('CONNECTION_FAILED', { defaultValue: 'Connection failed' }),
          description: t('TRY_CONNECTING_AGAIN', {
            defaultValue: 'The authorization did not complete. Please try again.',
          }),
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [toolId]);

  useEffect(() => {
    if (!pendingToolkit) return;
    const status = connections[pendingToolkit]?.status;
    if (status === 'ACTIVE' || status === 'FAILED' || status === 'EXPIRED') {
      clearPending();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections, pendingToolkit]);

  const handleConnect = async () => {
    if (!canManageConnection || !primaryToolkit) return;

    try {
      const response = await initiateMutation.mutateAsync({ toolId, toolkit: primaryToolkit });
      if (!response?.redirectUrl) {
        throw new Error(t('NO_REDIRECT_URL', { defaultValue: 'No authorization URL returned.' }));
      }
      window.open(response.redirectUrl, '_blank', 'width=600,height=700');
      setPendingToolkit(primaryToolkit);
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = setTimeout(() => setPendingToolkit(null), PENDING_TIMEOUT_MS);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('CONNECTION_FAILED', { defaultValue: 'Connection failed' }),
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_START_AUTHORIZATION', {
              defaultValue: 'Failed to start the authorization flow.',
            }),
      });
    }
  };

  const handleDisconnect = async () => {
    if (!canManageConnection || !primaryToolkit) return;

    try {
      const response = await disconnectMutation.mutateAsync({ toolId, toolkit: primaryToolkit });
      toast({
        variant: 'success',
        title: t('DISCONNECTED', { defaultValue: 'Disconnected' }),
        description: t('COMPOSIO_ACCOUNTS_REMOVED', {
          defaultValue: '{{count}} connected account(s) removed.',
          count: response?.removed ?? 0,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ['tools', 'composio-connections', toolId] });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('DISCONNECT_FAILED', { defaultValue: 'Disconnect failed' }),
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_DISCONNECT', { defaultValue: 'Failed to disconnect the account.' }),
      });
    }
  };

  const connectionButtonClassName = cn(
    'h-7 shrink-0 rounded-full border border-border/70 bg-muted/50 px-3 text-xs font-medium text-foreground shadow-none',
    'hover:bg-muted hover:text-foreground'
  );

  const renderConnectionButton = () => {
    if (!primaryToolkit || !canManageConnection) return null;

    if (connectionsQuery.isPending || (connectionsQuery.isFetching && !connectionsQuery.data)) {
      return <Skeleton className="h-7 w-20 shrink-0 rounded-full" />;
    }

    if (isConnected) {
      return (
        <button
          type="button"
          className={cn(
            connectionButtonClassName,
            'disabled:pointer-events-none disabled:opacity-50',
            'hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive'
          )}
          disabled={busy}
          onClick={() => void handleDisconnect()}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            t('DISCONNECT', { defaultValue: 'Disconnect' })
          )}
        </button>
      );
    }

    if (authConfigMissing) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <button
                  type="button"
                  className={cn(connectionButtonClassName, 'cursor-not-allowed opacity-50')}
                  disabled
                >
                  {t('CONNECT', { defaultValue: 'Connect' })}
                </button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-64 text-[13px] leading-5">
                {toolkitAvailability?.reason ||
                  t('AUTH_CONFIG_UNAVAILABLE', {
                    defaultValue:
                      'No auth configuration is available for this toolkit yet. Contact your administrator.',
                  })}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    const isConnecting = busy || isPendingThis;
    return (
      <button
        type="button"
        className={cn(
          connectionButtonClassName,
          'inline-flex min-w-20 items-center justify-center disabled:pointer-events-none disabled:opacity-50'
        )}
        disabled={isConnecting}
        onClick={() => void handleConnect()}
        aria-label={
          isConnecting
            ? t('CONNECTING', { defaultValue: 'Connecting' })
            : t('CONNECT', { defaultValue: 'Connect' })
        }
      >
        {isConnecting ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          t('CONNECT', { defaultValue: 'Connect' })
        )}
      </button>
    );
  };

  // Wait for the detail API — list-card fallback alone is not enough to render.
  const showSkeleton = isOpen && !toolQuery.data && (toolQuery.isPending || toolQuery.isFetching);
  const isCatalogLoading = catalogQuery.isPending || (catalogQuery.isFetching && !catalogQuery.data);

  const renderActionsContent = () => {
    if (actionLabels.length === 0) {
      return (
        <p className="text-sm leading-6 text-muted-foreground">
          {t('NO_ACTIONS_FOUND', { defaultValue: 'No actions found.' })}
        </p>
      );
    }

    return (
      <>
        <div className="flex flex-wrap gap-2">
          {visibleActions.map((action) => (
            <ActionChip key={action} label={action} />
          ))}
        </div>
        {hasMoreActions ? (
          <button
            type="button"
            className="text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
            onClick={() => setShowAllActions((current) => !current)}
          >
            {showAllActions
              ? t('SHOW_LESS', { defaultValue: 'Show less' })
              : t('SEE_MORE', { defaultValue: 'See more' })}
          </button>
        ) : null}
      </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[min(92dvh,820px)] w-[calc(100vw-1.5rem)] max-w-[540px] flex-col gap-0 overflow-hidden p-0">
        {showSkeleton ? (
          <ConnectorDetailsSkeleton />
        ) : (
          <>
            <div className="shrink-0 px-7 pb-2 pt-7">
              <div className="flex items-start gap-4 pr-8">
                {isCatalogLoading && !catalogEntry?.logo ? (
                  <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
                ) : (
                  <ConnectorLogo
                    logo={catalogEntry?.logo}
                    name={displayName}
                    toolkit={primaryToolkit}
                  />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 space-y-0.5">
                      <h2 className="truncate text-[22px] font-semibold leading-tight tracking-tight text-foreground">
                        {displayName}
                      </h2>
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <p className="text-sm text-muted-foreground">{categoryLabel}</p>
                        <VisibilityBadge
                          value={getToolVisibility(toolDetails ?? tool)}
                          className="shrink-0"
                        />
                      </div>
                    </div>

                    {renderConnectionButton()}
                  </div>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-7 pb-7 pt-3">
              {displayDescription ? (
                <p className="text-[15px] leading-7 text-muted-foreground">{displayDescription}</p>
              ) : null}

              {primaryToolkit ? (
                <div className="space-y-3">
                  {actionsHeading ? (
                    <p className="text-sm text-muted-foreground">{actionsHeading}</p>
                  ) : null}

                  {renderActionsContent()}
                </div>
              ) : null}

              {(primaryToolkit || isPendingThis) && (
                <div className="space-y-4 border-t border-border/70 pt-5">
                  <p className="text-sm text-muted-foreground">{t('DETAILS', { defaultValue: 'Details' })}</p>

                  {primaryToolkit ? (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">{t('APP', { defaultValue: 'App' })}</p>
                      <p className="text-sm font-semibold text-foreground">{displayName}</p>
                    </div>
                  ) : null}

                  {toolkits.length > 1 ? (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {t('ADDITIONAL_APPS', { defaultValue: 'Additional apps' })}
                      </p>
                      <p className="text-sm font-semibold text-foreground">
                        {toolkits.slice(1).map((toolkit) => formatLabel(toolkit)).join(', ')}
                      </p>
                    </div>
                  ) : null}

                  {/* {authConfigValue ? (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {t('AUTH_CONFIG', { defaultValue: 'Auth config' })}
                      </p>
                      <p className="break-all font-mono text-xs font-medium text-foreground">
                        {authConfigValue}
                      </p>
                    </div>
                  ) : null} */}

                  {/* {isPendingThis ? (
                    <p className="rounded-lg bg-muted/50 px-3 py-2 text-xs leading-5 text-muted-foreground">
                      {t('COMPLETE_AUTH_IN_POPUP', {
                        defaultValue:
                          'Complete the sign-in in the popup window. This page updates automatically.',
                      })}
                    </p>
                  ) : null} */}
                </div>
              )}

              <p className="text-xs leading-5 text-muted-foreground/90">
                <span>
                  {t('CONNECTOR_DISCLAIMER', {
                    defaultValue:
                      'Third-party connectors are not built or maintained by Selise. Use caution when granting access to external services. Review the permissions before connecting. See our',
                  })}{' '}
                  <a
                    href="https://app.selise.ai/en/privacy-policy/"
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2 hover:text-foreground"
                  >
                    {t('PRIVACY_POLICY', { defaultValue: 'Privacy Policy' })}
                  </a>
                  .
                </span>
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
